import React, { useState } from "react";
import { MAX_OPCIONES, nuevaVariante, nuevoAdicional } from "./opcionesDeServicio";

/* Variants box and add-ons section of the service form (ServicioForm). Pure presentation: the form owns the state. */

const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

const IconoQuitar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconoInfo = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

/** Money input in the compact size of the variant rows. */
const DineroChico = ({ value, onChange, label }) => (
  <div className="pu-money">
    <span>$</span>
    <input className="ef-input ef-input--sm" type="number" min="0" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

/** `zr-box` "Tiene variantes": switch + one row per variant. With variants the base duration and price are not used. */
export const VariantesBox = ({ activo, onToggle, variantes, onChange, semilla, precioDesde }) => {
  const cambiar = (key, patch) => onChange(variantes.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  return (
    <div className="zr-box">
      <div className="zr-row-toggle">
        <div className="zr-row-toggle__txt">
          <div className="t">Tiene variantes</div>
          <div className="h">El mismo servicio con distinto precio o duración, por ejemplo según el largo del pelo.</div>
        </div>
        <input type="checkbox" className="zr-switch" aria-label="Tiene variantes" checked={activo} onChange={(e) => onToggle(e.target.checked)} />
      </div>

      {activo && (
        <>
          <div className="zr-variant-grid">
            <div className="zr-variant-grid__head" aria-hidden="true">
              <span className="ef-label">Variante</span>
              <span className="ef-label">Duración (min)</span>
              <span className="ef-label">Precio</span>
              <span />
            </div>
            {variantes.map((v, i) => {
              const nombre = v.nombre.trim() || `la variante ${i + 1}`;
              return (
                <div key={v.key} className="zr-variant-grid__row">
                  <input
                    className="ef-input ef-input--sm"
                    aria-label={`Nombre de la variante ${i + 1}`}
                    placeholder="Ej: Pelo corto"
                    value={v.nombre}
                    onChange={(e) => cambiar(v.key, { nombre: e.target.value })}
                  />
                  <input
                    className="ef-input ef-input--sm"
                    type="number"
                    min="5"
                    step="5"
                    aria-label={`Duración de ${nombre}`}
                    value={v.duracion}
                    onChange={(e) => cambiar(v.key, { duracion: e.target.value })}
                  />
                  <DineroChico label={`Precio de ${nombre}`} value={v.precio} onChange={(precio) => cambiar(v.key, { precio })} />
                  <button
                    type="button"
                    className="zr-icon-btn"
                    aria-label={`Quitar ${v.nombre.trim() || `variante ${i + 1}`}`}
                    onClick={() => onChange(variantes.filter((x) => x.key !== v.key))}
                  >
                    <IconoQuitar />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="zr-box__foot">
            <button
              type="button"
              className="zr-btn zr-btn--soft zr-btn--sm"
              disabled={variantes.length >= MAX_OPCIONES}
              onClick={() => onChange([...variantes, nuevaVariante(variantes.length ? {} : semilla)])}
            >
              + Agregar variante
            </button>
            <p className="ef-footnote">
              El cliente elige una al reservar. En la lista se muestra «Desde {plata(precioDesde)}».
            </p>
          </div>
        </>
      )}
    </div>
  );
};

/** Section "Adicionales": optional extras the client adds to the service. */
export const AdicionalesSection = ({ adicionales, onChange }) => {
  const [editando, setEditando] = useState("");
  const cambiar = (key, patch) => onChange(adicionales.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const agregar = () => {
    const nuevo = nuevoAdicional();
    onChange([...adicionales, nuevo]);
    setEditando(nuevo.key);
  };

  return (
    <section className="zr-overrides-section" aria-labelledby="srv-adicionales-titulo">
      <h4 className="zr-overrides-title" id="srv-adicionales-titulo">Adicionales</h4>
      <p className="zr-overrides-hint">Extras opcionales que el cliente suma a este servicio. Cada uno agrega tiempo y precio.</p>

      {adicionales.map((a, i) => {
        const abierto = editando === a.key;
        const nombre = a.nombre.trim() || `adicional ${i + 1}`;
        return (
          <div key={a.key} className={`zr-pro-override-row zr-addon-row${abierto ? " is-editing" : ""}`}>
            <div className="zr-pro-override-info">
              <div>
                <div className="zr-pro-override-name">{a.nombre.trim() || "Adicional sin nombre"}</div>
                <div className="zr-pro-override-status">
                  {abierto ? "Editando" : `+ ${Number(a.duracion) || 0} min · + ${plata(a.precio)}`}
                </div>
              </div>
            </div>
            <div className="bt-ov-actions">
              {abierto ? (
                <button type="button" className="zr-btn zr-btn--dark zr-btn--sm" onClick={() => setEditando("")}>
                  Listo
                </button>
              ) : (
                <>
                  <button type="button" className="zr-btn zr-btn--ghost zr-btn--sm" aria-label={`Editar ${nombre}`} onClick={() => setEditando(a.key)}>
                    Editar
                  </button>
                  <button type="button" className="zr-btn zr-btn--ghost zr-btn--sm" aria-label={`Quitar ${nombre}`} onClick={() => onChange(adicionales.filter((x) => x.key !== a.key))}>
                    Quitar
                  </button>
                </>
              )}
            </div>
            {abierto && (
              <div className="zr-addon-grid">
                <div className="ef-field">
                  <label className="ef-label" htmlFor={`adicional-nombre-${a.key}`}>Nombre</label>
                  <input
                    id={`adicional-nombre-${a.key}`}
                    className="ef-input ef-input--sm"
                    aria-label={`Nombre del adicional ${i + 1}`}
                    placeholder="Ej: Peinado / brushing"
                    value={a.nombre}
                    onChange={(e) => cambiar(a.key, { nombre: e.target.value })}
                  />
                </div>
                <div className="ef-field">
                  <label className="ef-label" htmlFor={`adicional-min-${a.key}`}>Suma (min)</label>
                  <input
                    id={`adicional-min-${a.key}`}
                    className="ef-input ef-input--sm"
                    type="number"
                    min="0"
                    step="5"
                    aria-label={`Suma (min) de ${nombre}`}
                    value={a.duracion}
                    onChange={(e) => cambiar(a.key, { duracion: e.target.value })}
                  />
                </div>
                <div className="ef-field">
                  <label className="ef-label" htmlFor={`adicional-precio-${a.key}`}>Suma precio</label>
                  <div className="pu-money">
                    <span>$</span>
                    <input
                      id={`adicional-precio-${a.key}`}
                      className="ef-input ef-input--sm"
                      type="number"
                      min="0"
                      aria-label={`Suma precio de ${nombre}`}
                      value={a.precio}
                      onChange={(e) => cambiar(a.key, { precio: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div>
        <button type="button" className="zr-btn zr-btn--soft zr-btn--sm" disabled={adicionales.length >= MAX_OPCIONES} onClick={agregar}>
          + Agregar adicional
        </button>
      </div>
    </section>
  );
};

/** Note shown in "Precio y duración por profesional" when the service has variants. */
export const AvisoConVariantes = () => (
  <div className="zr-info">
    <IconoInfo />
    <span>Con variantes, todos usan los precios y duraciones de cada variante. Acá solo elegís quién lo realiza.</span>
  </div>
);
